import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateSessionId() {
  return "chat_" + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

function getSessionId() {
  let sid = sessionStorage.getItem("faith_chat_session");
  if (!sid) {
    sid = generateSessionId();
    sessionStorage.setItem("faith_chat_session", sid);
  }
  return sid;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      <span className="w-1.5 h-1.5 bg-[#C8A97E] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1.5 h-1.5 bg-[#C8A97E] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-1.5 h-1.5 bg-[#C8A97E] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  );
}

function LinkifyText({ text }) {
  // Convert markdown links [text](/path) to clickable links
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const match = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (match) {
      return (
        <a key={i} href={match[2]} className="text-[#C8A97E] underline hover:text-[#B3956A] font-medium" target={match[2].startsWith("http") ? "_blank" : "_self"} rel="noopener noreferrer">
          {match[1]}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        data-testid={`chat-msg-${msg.role}`}
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#1C2E4A] text-white rounded-br-md"
            : "bg-[#F0EDE8] text-[#333] rounded-bl-md"
        }`}
      >
        <LinkifyText text={msg.content} />
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! I'm Faith Assistant. How can I help you today? Whether you have questions about starting a church school, our programs, or anything else on our site, I'm here to help." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const sessionId = getSessionId();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setStreaming(true);

    // Add placeholder for assistant response
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      if (!resp.ok) throw new Error("Chat request failed");
      const data = await resp.json();

      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: "assistant", content: data.response || "I'm sorry, I couldn't generate a response." };
        return updated;
      });
    } catch {
      setMessages((prev) => {
        const updated = [...prev];
        if (updated[updated.length - 1]?.role === "assistant" && !updated[updated.length - 1]?.content) {
          updated[updated.length - 1] = { role: "assistant", content: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment." };
        }
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  };

  const clearChat = async () => {
    const sessionId = sessionStorage.getItem("faith_chat_session");
    if (sessionId) {
      fetch(`${API}/chat/${sessionId}`, { method: "DELETE" }).catch(() => {});
      sessionStorage.removeItem("faith_chat_session");
    }
    setMessages([
      { role: "assistant", content: "Hello! I'm Faith Assistant. How can I help you today? Whether you have questions about starting a church school, our programs, or anything else on our site, I'm here to help." },
    ]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat bubble */}
      {!open && (
        <button
          data-testid="chat-widget-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] w-14 h-14 bg-[#1C2E4A] hover:bg-[#152238] text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
          aria-label="Open Faith Assistant chat"
        >
          <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#C8A97E] rounded-full border-2 border-white" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-6 right-6 z-[9999] w-[380px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-48px)] bg-white rounded-2xl shadow-2xl border border-[#E5E2DC] flex flex-col overflow-hidden"
          style={{ animation: "chatSlideUp 0.25s ease-out" }}
        >
          {/* Header */}
          <div className="bg-[#1C2E4A] px-5 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#C8A97E]/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4.5 h-4.5 text-[#C8A97E]" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Faith Assistant</h3>
                <p className="text-white/50 text-xs">ACS Virtual Church</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="chat-clear-btn"
                onClick={clearChat}
                className="p-2 text-white/50 hover:text-white/80 transition-colors rounded-lg hover:bg-white/10"
                title="Clear conversation"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                data-testid="chat-close-btn"
                onClick={() => setOpen(false)}
                className="p-2 text-white/50 hover:text-white/80 transition-colors rounded-lg hover:bg-white/10"
                title="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1" style={{ scrollbarWidth: "thin" }}>
            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}
            {streaming && messages[messages.length - 1]?.content === "" && <TypingDots />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-[#E5E2DC] px-4 py-3 shrink-0 bg-[#FAFAF8]">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything..."
                rows={1}
                className="flex-1 resize-none border border-[#E5E2DC] rounded-xl px-3.5 py-2.5 text-sm text-[#333] placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#C8A97E]/30 focus:border-[#C8A97E] bg-white max-h-24"
                style={{ minHeight: "40px" }}
                disabled={streaming}
              />
              <button
                data-testid="chat-send-btn"
                onClick={sendMessage}
                disabled={!input.trim() || streaming}
                className="w-10 h-10 bg-[#1C2E4A] hover:bg-[#152238] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center justify-center shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}
